import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError } from "../errors";
import schemaValidators from "./schema-validators";
import { AssessmentInfo, VerityOptions } from "../types";

type StandaloneOptions = {
	studentInfoEntries: {
		email: string;
		firstName: string;
		lastName: string;
	}[];
	instructionTemplate: string;
	emailBody: string;
	expiryDate: Date | undefined;
	exemptedEmails: number[];
};

type Application = {
	id: number;
	name: string;
	processName: string[];
	iconSvg?: string;
	type: "Browser" | "Communication" | "Screen Sharing" | "Other";
};

export default class VerityOptionsClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	get = async ({
		isManagedGuestSession = false,
		...quizInfo
	}: AssessmentInfo & {
		isManagedGuestSession?: boolean;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms}/${quizInfo.hostname}/${quizInfo.courseId ?? ""}/${
					quizInfo.quizId
				}?isManagedGuestSession=${isManagedGuestSession}`,
				{ credentials: "include" },
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.verityOptions.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	save = async ({
		quizInfo,
		courseInfo,
		options,
		ignore,
		standaloneOptions,
	}: {
		quizInfo: AssessmentInfo;
		courseInfo?: {
			name?: string;
			code?: string;
		};
		options?: Partial<VerityOptions>;
		ignore?: Array<keyof VerityOptions>;
		standaloneOptions?: StandaloneOptions;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms}/${quizInfo.hostname}/${quizInfo.courseId ?? ""}/${
					quizInfo.quizId
				}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						quizInfo: quizInfo,
						courseInfo: courseInfo,
						verityOptions: options,
						ignore: ignore,
						standaloneOptions: standaloneOptions,
					}),
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAllApplications = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/applications/all`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.applicationResponse.validate(obj)) {
					const APPLICATION_LIST: Application[] = obj.map((app) => ({
						id: app.id,
						name: app.name,
						processName: app.processName,
						iconSvg: app.iconSvg,
						type: app.type,
					}));
					return APPLICATION_LIST;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
